import React, { useState } from "react";
import { Session, TrainingLog } from "../../types/post";
import SessionLinkModal from "./SessionLinkModal";

interface UnlinkedSessionsSectionProps {
  // Completed sessions for the day that are not yet linked to a training log.
  sessions: (Session & { id: string })[];
  // Training logs on the same day the athlete can link a session to.
  trainingLogs: (TrainingLog & { id: string })[];
  // Open the training log form pre-linked to this session (create new log).
  onCreateLog: (session: Session & { id: string }) => void;
  // Link the session to an existing training log.
  onLinkToLog: (sessionId: string, trainingLogId: string) => void | Promise<void>;
  // Card tap: the Dashboard opens the session bottom sheet (view, link,
  // change date, delete / reject) rather than navigating away.
  onViewSession: (session: Session & { id: string }) => void;
  // Resolve the display title for a session.
  getSessionTitle: (session: Session) => string;
  // Session currently being linked (shows a busy state / disables actions).
  linkingSessionId?: string | null;
}

// Surfaces completed coach/athlete sessions that have not yet been linked to a
// training log, letting the athlete either create a new training log for the
// session or link it to an existing one for the day.
const UnlinkedSessionsSection: React.FC<UnlinkedSessionsSectionProps> = ({
  sessions,
  trainingLogs,
  onCreateLog,
  onLinkToLog,
  onViewSession,
  getSessionTitle,
  linkingSessionId,
}) => {
  const [linkModalSessionId, setLinkModalSessionId] = useState<string | null>(
    null,
  );

  if (sessions.length === 0) return null;

  const linkModalSession =
    sessions.find((session) => session.id === linkModalSessionId) || null;

  return (
    <div className="bg-base-100 rounded-none md:rounded-lg px-8 py-4 mt-3">
      <h3 className="text-sm font-heading px-0 mb-1">
        Sessions{" "}
        <span className="font-grotesk text-base-content/60">to log</span>
      </h3>
      <p className="text-xs text-base-content/50 font-grotesk mb-4">
        Completed sessions not yet linked to a training log.
      </p>
      <div className="space-y-3">
        {sessions.map((session) => {
          const activityCount = session.ActivityMeasurementList?.length ?? 0;
          const isLinking = linkingSessionId === session.id;
          const coachName =
            session.coach?.name || session.coachID
              ? session.coach?.name
                ? `${session.coach.name}${
                    session.coach.surname ? ` ${session.coach.surname}` : ""
                  }`
                : "Coach"
              : null;

          return (
            <div
              key={session.id}
              onClick={() => onViewSession(session)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onViewSession(session);
                }
              }}
              className="p-4 rounded-xl border bg-base-200 border-base-300 hover:border-base-content/60 hover:bg-base-300 transition-colors cursor-pointer"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-base-content font-heading truncate">
                    {getSessionTitle(session)}
                  </div>
                  <div className="text-xs text-base-content/60 font-grotesk mt-0.5">
                    {activityCount}{" "}
                    {activityCount === 1 ? "activity" : "activities"}
                    {coachName && <> · Set by {coachName}</>}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    disabled={isLinking}
                    onClick={(e) => {
                      e.stopPropagation();
                      setLinkModalSessionId(session.id);
                    }}
                    className="btn btn-primary btn-sm min-h-11 h-11 rounded-xl px-3 text-xs font-medium font-grotesk normal-case"
                  >
                    Link
                  </button>
                  <button
                    type="button"
                    disabled={isLinking}
                    onClick={(e) => {
                      e.stopPropagation();
                      onCreateLog(session);
                    }}
                    className="btn btn-ghost btn-sm min-h-11 h-11 rounded-xl px-3 text-xs font-medium border border-base-300 text-base-content/70 bg-base-100 font-grotesk normal-case hover:border-base-content/40 hover:bg-base-100"
                  >
                    Create Log
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {linkModalSession && (
        <SessionLinkModal
          isOpen={!!linkModalSession}
          onClose={() => setLinkModalSessionId(null)}
          session={linkModalSession}
          trainingLogs={trainingLogs}
          onLinkToLog={onLinkToLog}
          onCreateLog={onCreateLog}
          getSessionTitle={getSessionTitle}
        />
      )}
    </div>
  );
};

export default UnlinkedSessionsSection;
